import { cloneDeep } from "lodash";
import * as client from "../../../client";
import { formatMonetaryValue } from "./util";

/**
 * Pure state transforms for editing and deleting policy deductibles, limits,
 * and sublimits. These mirror the persistence semantics the backend expects:
 *
 *   - Wholly-manual entries are hard-rekeyed/deleted, since nothing
 *     re-extracts them on a report rerun.
 *   - Extracted entries are soft-deleted (via `removed_excerpt_names` or
 *     `is_removed`) so the change survives re-extraction.
 *
 * Keeping these pure means they can be unit-tested without the React tree.
 */

/** Common excerpt-bearing shape shared by AdvancedDeductible and AdvancedLimit. */
type ExcerptEntry = {
  direct_excerpts?: { [key: string]: string } | null;
  manual_excerpt_overrides?: { [key: string]: string } | null;
  removed_excerpt_names?: Array<string> | null;
  excerpt_sort_keys?: { [key: string]: string } | null;
};

/** Copy of `overrides` with any key matching `name` case-insensitively removed.
 *  Callers re-set `name` afterward to keep one override key per excerpt, since
 *  the read layer treats casefold-equal names as the same excerpt. */
const dropOverrideCasefold = (
  overrides: { [key: string]: string } | null | undefined,
  name: string
): { [key: string]: string } => {
  const key = name.toLowerCase();
  return Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([k]) => k.toLowerCase() !== key)
  );
};

export type AdvancedEditParams = {
  perilType: client.peril_type;
  /** Selected extracted policy form code; needed to locate extracted entries. */
  policyCode: string | undefined;
  oldName: string;
  newName: string;
  newValue: string;
};

export type AdvancedDeleteParams = {
  perilType: client.peril_type;
  policyCode: string | undefined;
  name: string;
};

/** True when the user-entered name is non-empty and differs from the original. */
export const isRename = (oldName: string, newName: string): boolean => {
  const trimmed = newName.trim();
  return trimmed !== "" && trimmed !== oldName;
};

/**
 * Apply a value edit (and optional rename) to a single excerpt entry, in place.
 *
 * `hardRekey` controls how a rename drops the old name: true for wholly-manual
 * entries (delete outright from both excerpt maps), false for extracted entries
 * (soft-delete via removed_excerpt_names so it stays gone across reruns).
 */
const editExcerptEntry = (
  entry: ExcerptEntry,
  oldName: string,
  newName: string,
  newValue: string,
  hardRekey: boolean
): void => {
  const value = formatMonetaryValue(newValue);
  const rename = isRename(oldName, newName);
  const targetName = rename ? newName.trim() : oldName;

  entry.manual_excerpt_overrides = {
    ...dropOverrideCasefold(entry.manual_excerpt_overrides, targetName),
    [targetName]: value,
  };
  if (!rename) return;

  delete entry.manual_excerpt_overrides[oldName];
  if (hardRekey) {
    if (entry.direct_excerpts) delete entry.direct_excerpts[oldName];
  } else {
    const removed = new Set(entry.removed_excerpt_names ?? []);
    removed.add(oldName);
    entry.removed_excerpt_names = Array.from(removed);
  }
};

/** Delete a named excerpt from a single entry, in place. Returns true if the
 *  entry still holds any excerpts afterward (only meaningful for manual entries). */
const deleteExcerptFromManualEntry = (
  entry: ExcerptEntry,
  name: string
): boolean => {
  if (entry.manual_excerpt_overrides) {
    delete entry.manual_excerpt_overrides[name];
  }
  if (entry.direct_excerpts) {
    delete entry.direct_excerpts[name];
  }
  return (
    Object.keys(entry.direct_excerpts ?? {}).length > 0 ||
    Object.keys(entry.manual_excerpt_overrides ?? {}).length > 0
  );
};

/** Soft-delete a named excerpt from an extracted entry, in place. */
const softDeleteExcerpt = (entry: ExcerptEntry, name: string): void => {
  // Clearing the override is what makes the removal stick: a surviving override
  // beats a removal (see effectiveExcerpts), and its key may differ in casing.
  entry.manual_excerpt_overrides = dropOverrideCasefold(
    entry.manual_excerpt_overrides,
    name
  );
  const removed = new Set(entry.removed_excerpt_names ?? []);
  removed.add(name);
  entry.removed_excerpt_names = Array.from(removed);
};

const editAdvancedEntry = <E extends ExcerptEntry>(
  manual: { [key: string]: E },
  extractedByPolicy: { [key: string]: { [key: string]: E } },
  params: AdvancedEditParams
): void => {
  const manualEntry = manual[params.perilType];
  if (manualEntry) {
    editExcerptEntry(
      manualEntry,
      params.oldName,
      params.newName,
      params.newValue,
      true
    );
    return;
  }
  if (params.policyCode) {
    const extractedEntry =
      extractedByPolicy[params.policyCode]?.[params.perilType];
    if (extractedEntry) {
      editExcerptEntry(
        extractedEntry,
        params.oldName,
        params.newName,
        params.newValue,
        false
      );
    }
  }
};

const deleteAdvancedEntry = <E extends ExcerptEntry>(
  manual: { [key: string]: E },
  extractedByPolicy: { [key: string]: { [key: string]: E } },
  params: AdvancedDeleteParams
): void => {
  const manualEntry = manual[params.perilType];
  if (manualEntry) {
    const hasAny = deleteExcerptFromManualEntry(manualEntry, params.name);
    if (!hasAny) delete manual[params.perilType];
    return;
  }
  if (params.policyCode) {
    const extractedEntry =
      extractedByPolicy[params.policyCode]?.[params.perilType];
    if (extractedEntry) softDeleteExcerpt(extractedEntry, params.name);
  }
};

export const applyDeductibleEdit = (
  prev: client.AdvancedDeductibleInfo | null | undefined,
  params: AdvancedEditParams
): client.AdvancedDeductibleInfo => {
  const next = cloneDeep(
    prev ?? { extracted_deductibles: {}, manual_deductibles: {} }
  );
  editAdvancedEntry(
    next.manual_deductibles,
    next.extracted_deductibles,
    params
  );
  return next;
};

export const applyDeductibleDelete = (
  prev: client.AdvancedDeductibleInfo | null | undefined,
  params: AdvancedDeleteParams
): client.AdvancedDeductibleInfo => {
  const next = cloneDeep(
    prev ?? { extracted_deductibles: {}, manual_deductibles: {} }
  );
  deleteAdvancedEntry(
    next.manual_deductibles,
    next.extracted_deductibles,
    params
  );
  return next;
};

export const applyLimitEdit = (
  prev: client.AdvancedLimitInfo | null | undefined,
  params: AdvancedEditParams
): client.AdvancedLimitInfo => {
  const next = cloneDeep(prev ?? { extracted_limits: {}, manual_limits: {} });
  editAdvancedEntry(next.manual_limits, next.extracted_limits, params);
  return next;
};

export const applyLimitDelete = (
  prev: client.AdvancedLimitInfo | null | undefined,
  params: AdvancedDeleteParams
): client.AdvancedLimitInfo => {
  const next = cloneDeep(prev ?? { extracted_limits: {}, manual_limits: {} });
  deleteAdvancedEntry(next.manual_limits, next.extracted_limits, params);
  return next;
};

export type AdvancedAddParams = {
  perilType: client.peril_type;
  policyCode: string | undefined;
  name: string;
  value: string;
};

/** Add a manual excerpt under a peril. Layers onto the entry that actually
 *  renders for that peril (manual shadows extracted), matching buildLimitRows /
 *  buildDeductibleRows precedence; otherwise creates a manual entry. */
const addExcerptToEntry = (
  entry: ExcerptEntry,
  name: string,
  value: string
): void => {
  entry.manual_excerpt_overrides = {
    ...dropOverrideCasefold(entry.manual_excerpt_overrides, name),
    [name]: value,
  };
  if (entry.removed_excerpt_names) {
    const key = name.toLowerCase();
    entry.removed_excerpt_names = entry.removed_excerpt_names.filter(
      (n) => n.toLowerCase() !== key
    );
  }
};

export const applyLimitAdd = (
  prev: client.AdvancedLimitInfo | null | undefined,
  params: AdvancedAddParams
): client.AdvancedLimitInfo => {
  const next = cloneDeep(prev ?? { extracted_limits: {}, manual_limits: {} });
  const value = formatMonetaryValue(params.value);
  const extractedExisting = params.policyCode
    ? next.extracted_limits[params.policyCode]?.[params.perilType]
    : undefined;
  const target = next.manual_limits[params.perilType] ?? extractedExisting;
  if (target) {
    addExcerptToEntry(target, params.name, value);
  } else {
    next.manual_limits[params.perilType] = {
      peril_type: params.perilType,
      short_limit_description: "",
      document_quotation: "",
      executable_python_function_str: "",
      direct_excerpts: {},
      manual_excerpt_overrides: { [params.name]: value },
    };
  }
  return next;
};

export const applyDeductibleAdd = (
  prev: client.AdvancedDeductibleInfo | null | undefined,
  params: AdvancedAddParams
): client.AdvancedDeductibleInfo => {
  const next = cloneDeep(
    prev ?? { extracted_deductibles: {}, manual_deductibles: {} }
  );
  const value = formatMonetaryValue(params.value);
  const extractedExisting = params.policyCode
    ? next.extracted_deductibles[params.policyCode]?.[params.perilType]
    : undefined;
  const target = next.manual_deductibles[params.perilType] ?? extractedExisting;
  if (target) {
    addExcerptToEntry(target, params.name, value);
  } else {
    next.manual_deductibles[params.perilType] = {
      peril_type: params.perilType,
      short_deductible_description: "",
      executable_python_function_str: "",
      direct_excerpts: {},
      manual_excerpt_overrides: { [params.name]: value },
    };
  }
  return next;
};

export type SublimitEditParams = {
  manual: boolean;
  policyCode: string | undefined;
  oldName: string;
  classification: client.Sublimit["classification"];
  documentExcerpt: string | null;
  newName: string;
  newValue: string;
};

export type SublimitDeleteParams = {
  manual: boolean;
  policyCode: string | undefined;
  name: string;
  description: string;
  classification: client.Sublimit["classification"];
};

const matchesRow = (
  limit: client.Sublimit,
  name: string,
  classification: client.Sublimit["classification"]
): boolean =>
  limit.sublimit_name === name && limit.classification === classification;

/**
 * Produce the SublimitInfo for a sublimit value edit (and optional rename).
 * Sublimits persist immediately, so this returns the full payload to send.
 */
export const applySublimitEdit = (
  info: client.SublimitInfo,
  params: SublimitEditParams
): client.SublimitInfo => {
  const value = formatMonetaryValue(params.newValue);
  const rename = isRename(params.oldName, params.newName);
  const targetName = rename ? params.newName.trim() : params.oldName;

  if (params.manual) {
    // Manual sublimit — nothing re-extracts it, so rename in place by
    // rewriting the sublimit_name field.
    return {
      extracted_sublimits: info.extracted_sublimits,
      manual_sublimits: info.manual_sublimits.map((limit) =>
        matchesRow(limit, params.oldName, params.classification)
          ? {
              ...limit,
              sublimit_name: targetName,
              sublimit_description: value,
            }
          : limit
      ),
    };
  }

  if (!params.policyCode) return info;
  const policyEntries = info.extracted_sublimits[params.policyCode] ?? [];

  if (rename) {
    // The extracted name re-extracts on rerun, so soft-delete it and recreate
    // the row under the new name as a manual sublimit that persists.
    return {
      extracted_sublimits: {
        ...info.extracted_sublimits,
        [params.policyCode]: policyEntries.map((limit) =>
          matchesRow(limit, params.oldName, params.classification)
            ? { ...limit, is_removed: true }
            : limit
        ),
      },
      manual_sublimits: [
        ...info.manual_sublimits,
        {
          sublimit_name: targetName,
          sublimit_description: value,
          document_excerpt: params.documentExcerpt ?? null,
          classification: params.classification ?? null,
        },
      ],
    };
  }

  return {
    extracted_sublimits: {
      ...info.extracted_sublimits,
      [params.policyCode]: policyEntries.map((limit) =>
        matchesRow(limit, params.oldName, params.classification)
          ? { ...limit, manual_override: value }
          : limit
      ),
    },
    manual_sublimits: info.manual_sublimits,
  };
};

/** Produce the SublimitInfo for a sublimit delete. */
export const applySublimitDelete = (
  info: client.SublimitInfo,
  params: SublimitDeleteParams
): client.SublimitInfo => {
  if (params.manual) {
    return {
      extracted_sublimits: info.extracted_sublimits,
      manual_sublimits: info.manual_sublimits.filter(
        (limit) =>
          limit.sublimit_name !== params.name ||
          limit.sublimit_description !== params.description ||
          limit.classification !== params.classification
      ),
    };
  }

  if (!params.policyCode) return info;
  const policyEntries = info.extracted_sublimits[params.policyCode] ?? [];
  return {
    extracted_sublimits: {
      ...info.extracted_sublimits,
      [params.policyCode]: policyEntries.map((limit) =>
        matchesRow(limit, params.name, params.classification)
          ? { ...limit, is_removed: true }
          : limit
      ),
    },
    manual_sublimits: info.manual_sublimits,
  };
};
